import React, { useState } from 'react';
import axios from 'axios';

const BACKEND_URL = window.location.origin || process.env.REACT_APP_BACKEND_URL;

const BrandCreation = ({ user }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [formData, setFormData] = useState({
    businessName: '',
    tagline: '',
    industry: '',
    description: '',
    logoStyle: 'modern',
    colorPreference: 'ai-choose',
    primaryColor: '#FF6B35',
    secondaryColor: '#F7931E'
  });
  const [brandKit, setBrandKit] = useState(null);
  const [error, setError] = useState('');

  // Check if user has access (Pro or Enterprise)
  const hasAccess = ['pro', 'enterprise'].includes(user?.subscription_tier?.toLowerCase());

  const industries = [
    'Technology', 'Healthcare', 'Finance', 'Education', 'Retail',
    'Food & Restaurant', 'Real Estate', 'Legal', 'Marketing',
    'Construction', 'Automotive', 'Entertainment', 'Fashion',
    'Fitness', 'Travel', 'Other'
  ];

  const logoStyles = [
    { value: 'modern', label: 'Modern', desc: 'Clean, minimal, contemporary' },
    { value: 'classic', label: 'Classic', desc: 'Timeless, elegant, traditional' },
    { value: 'bold', label: 'Bold', desc: 'Strong, impactful, confident' },
    { value: 'playful', label: 'Playful', desc: 'Fun, creative, energetic' },
    { value: 'minimal', label: 'Minimal', desc: 'Simple, refined, sophisticated' },
    { value: 'luxury', label: 'Luxury', desc: 'Premium, exclusive, elegant' }
  ];

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    setError('');
  };

  const handleNext = () => {
    // Validate current step
    if (step === 1) {
      if (!formData.businessName.trim()) {
        setError('Business name is required');
        return;
      }
      if (!formData.industry) {
        setError('Please select an industry');
        return;
      }
    }
    
    if (step === 2) {
      if (!formData.description.trim() || formData.description.length < 20) {
        setError('Please provide a detailed business description (at least 20 characters)');
        return;
      }
    }

    setError('');
    setStep(step + 1);
  };

  const handleBack = () => {
    setStep(step - 1);
    setError('');
  };

  const handleGenerate = async () => {
    setLoading(true);
    setProgress(0);
    setError('');
    
    try {
      // Start generation
      setProgressMessage('Initializing brand creation...');
      setProgress(10);

      const response = await axios.post(
        `${BACKEND_URL}/api/brand-kit/generate`,
        {
          user_id: user.id,
          ...formData
        }
      );

      // Simulate progress updates (in real implementation, use websocket or polling)
      const progressSteps = [
        { progress: 20, message: 'Generating logo concepts...' },
        { progress: 40, message: 'Creating business card design...' },
        { progress: 60, message: 'Designing social media templates...' },
        { progress: 80, message: 'Preparing letterhead and email signature...' },
        { progress: 95, message: 'Finalizing brand kit...' },
        { progress: 100, message: 'Complete!' }
      ];

      for (const stepData of progressSteps) {
        await new Promise(resolve => setTimeout(resolve, 3000));
        setProgress(stepData.progress);
        setProgressMessage(stepData.message);
      }

      setBrandKit(response.data);
      setStep(4);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate brand kit. Please try again.');
      setProgress(0);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (fileType) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/brand-kit/download/${brandKit.kit_id}/${fileType}`,
        { responseType: 'blob' }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Determine filename and extension based on file type
      let filename;
      if (fileType === 'complete') {
        filename = `${formData.businessName.replace(/\s+/g, '-')}-BrandKit-Complete.zip`;
      } else if (fileType === 'email-signature') {
        filename = `${formData.businessName.replace(/\s+/g, '-')}-EmailSignature.html`;
      } else if (fileType === 'brand-guide') {
        filename = `${formData.businessName.replace(/\s+/g, '-')}-BrandGuide.txt`;
      } else {
        filename = `${formData.businessName.replace(/\s+/g, '-')}-${fileType}.png`;
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
      alert('❌ Failed to download: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDownloadAll = () => {
    handleDownload('complete');
  };

  // Access denied screen
  if (!hasAccess) {
    return (
      <div className="p-8">
        <div className="max-w-2xl mx-auto text-center">
          <div className="text-6xl mb-6">🎨</div>
          <h2 className="text-3xl font-bold mb-4 gradient-text">Brand Creation Kit</h2>
          <p className="text-xl mb-8" style={{ color: 'var(--text-secondary)' }}>
            Create a complete professional brand identity with AI
          </p>
          
          <div className="p-8 rounded-xl mb-8" style={{ backgroundColor: 'var(--card-bg)', border: '2px solid var(--border-color)' }}>
            <h3 className="text-2xl font-bold mb-4 text-red-500">⚠️ Pro/Enterprise Feature</h3>
            <p className="text-lg mb-6" style={{ color: 'var(--text-secondary)' }}>
              Brand Creation Kit is available for Pro and Enterprise plan users only.
            </p>
            <p className="mb-6">
              Upgrade to access this powerful feature and create unlimited professional brand kits!
            </p>
            <button className="px-8 py-4 accent-gradient text-white rounded-lg font-bold text-lg btn-glow">
              Upgrade to Pro
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-left">
            <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--card-bg)' }}>
              <div className="text-2xl mb-2">📝</div>
              <h4 className="font-bold mb-2">Logo Design</h4>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>3 unique logo variations</p>
            </div>
            <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--card-bg)' }}>
              <div className="text-2xl mb-2">💼</div>
              <h4 className="font-bold mb-2">Business Assets</h4>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Cards, letterhead, signatures</p>
            </div>
            <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--card-bg)' }}>
              <div className="text-2xl mb-2">📱</div>
              <h4 className="font-bold mb-2">Social Media</h4>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Instagram, Facebook, LinkedIn</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-4 gradient-text">🎨 Brand Creation Kit</h1>
          <p className="text-lg" style={{ color: 'var(--text-secondary)' }}>
            Create your complete professional brand identity in minutes
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          {[1, 2, 3, 4].map((s) => (
            <React.Fragment key={s}>
              <div className={`flex items-center justify-center w-10 h-10 rounded-full font-bold ${
                s < step ? 'bg-green-500 text-white' :
                s === step ? 'accent-gradient text-white' :
                'bg-gray-700 text-gray-400'
              }`}>
                {s < step ? '✓' : s}
              </div>
              {s < 4 && (
                <div className={`h-1 w-16 ${s < step ? 'bg-green-500' : 'bg-gray-700'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-900/30 border border-red-500 rounded-lg text-red-200">
            {error}
          </div>
        )}

        {/* Step 1: Business Info */}
        {step === 1 && (
          <div className="p-8 rounded-xl" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
            <h2 className="text-2xl font-bold mb-6">Step 1: Business Information</h2>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-bold mb-2">Business Name *</label>
                <input
                  type="text"
                  value={formData.businessName}
                  onChange={(e) => handleInputChange('businessName', e.target.value)}
                  placeholder="Enter your business name"
                  className="w-full px-4 py-3 rounded-lg"
                  style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                />
              </div>

              <div>
                <label className="block text-sm font-bold mb-2">Tagline/Slogan (Optional)</label>
                <input
                  type="text"
                  value={formData.tagline}
                  onChange={(e) => handleInputChange('tagline', e.target.value)}
                  placeholder="Your memorable tagline"
                  className="w-full px-4 py-3 rounded-lg"
                  style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                />
              </div>

              <div>
                <label className="block text-sm font-bold mb-2">Industry *</label>
                <select
                  value={formData.industry}
                  onChange={(e) => handleInputChange('industry', e.target.value)}
                  className="w-full px-4 py-3 rounded-lg"
                  style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                >
                  <option value="">Select your industry</option>
                  {industries.map(ind => (
                    <option key={ind} value={ind}>{ind}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex justify-end mt-8">
              <button
                onClick={handleNext}
                className="px-8 py-3 accent-gradient text-white rounded-lg font-bold btn-glow"
              >
                Next →
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Business Description */}
        {step === 2 && (
          <div className="p-8 rounded-xl" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
            <h2 className="text-2xl font-bold mb-6">Step 2: Tell Us About Your Business</h2>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-bold mb-2">Business Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Describe your business, what you do, your values, and what makes you unique... (minimum 20 characters)"
                  rows={6}
                  className="w-full px-4 py-3 rounded-lg"
                  style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                />
                <p className="text-sm mt-2" style={{ color: 'var(--text-secondary)' }}>
                  {formData.description.length} characters (minimum 20)
                </p>
              </div>
            </div>

            <div className="flex justify-between mt-8">
              <button
                onClick={handleBack}
                className="px-8 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-bold"
              >
                ← Back
              </button>
              <button
                onClick={handleNext}
                className="px-8 py-3 accent-gradient text-white rounded-lg font-bold btn-glow"
              >
                Next →
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Style Preferences */}
        {step === 3 && !loading && (
          <div className="p-8 rounded-xl" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
            <h2 className="text-2xl font-bold mb-6">Step 3: Style Preferences</h2>
            
            <div className="space-y-8">
              <div>
                <label className="block text-sm font-bold mb-4">Logo Style</label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {logoStyles.map(style => (
                    <button
                      key={style.value}
                      onClick={() => handleInputChange('logoStyle', style.value)}
                      className={`p-4 rounded-lg border-2 transition ${
                        formData.logoStyle === style.value
                          ? 'border-orange-500 bg-orange-500/20'
                          : 'border-gray-700 hover:border-gray-600'
                      }`}
                    >
                      <div className="font-bold mb-1">{style.label}</div>
                      <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>{style.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold mb-4">Color Preference</label>
                <div className="space-y-4">
                  <button
                    onClick={() => handleInputChange('colorPreference', 'ai-choose')}
                    className={`w-full p-4 rounded-lg border-2 transition text-left ${
                      formData.colorPreference === 'ai-choose'
                        ? 'border-orange-500 bg-orange-500/20'
                        : 'border-gray-700 hover:border-gray-600'
                    }`}
                  >
                    <div className="font-bold mb-1">🤖 Let AI Choose (Recommended)</div>
                    <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                      AI will select perfect colors based on your industry and style
                    </div>
                  </button>

                  <button
                    onClick={() => handleInputChange('colorPreference', 'custom')}
                    className={`w-full p-4 rounded-lg border-2 transition text-left ${
                      formData.colorPreference === 'custom'
                        ? 'border-orange-500 bg-orange-500/20'
                        : 'border-gray-700 hover:border-gray-600'
                    }`}
                  >
                    <div className="font-bold mb-1">🎨 Choose Custom Colors</div>
                    <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                      Select your own brand colors
                    </div>
                  </button>

                  {formData.colorPreference === 'custom' && (
                    <div className="flex gap-4 mt-4">
                      <div className="flex-1">
                        <label className="block text-sm mb-2">Primary Color</label>
                        <input
                          type="color"
                          value={formData.primaryColor}
                          onChange={(e) => handleInputChange('primaryColor', e.target.value)}
                          className="w-full h-12 rounded-lg"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="block text-sm mb-2">Secondary Color</label>
                        <input
                          type="color"
                          value={formData.secondaryColor}
                          onChange={(e) => handleInputChange('secondaryColor', e.target.value)}
                          className="w-full h-12 rounded-lg"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-between mt-8">
              <button
                onClick={handleBack}
                className="px-8 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-bold"
              >
                ← Back
              </button>
              <button
                onClick={handleGenerate}
                className="px-8 py-3 accent-gradient text-white rounded-lg font-bold btn-glow"
              >
                🚀 Generate Brand Kit
              </button>
            </div>
          </div>
        )}

        {/* Loading State with Progress */}
        {loading && (
          <div className="p-12 rounded-xl text-center" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
            <div className="mb-8">
              <div className="inline-block animate-spin text-6xl mb-4">🎨</div>
              <h2 className="text-2xl font-bold mb-2">Creating Your Brand Kit</h2>
              <p style={{ color: 'var(--text-secondary)' }}>{progressMessage}</p>
            </div>

            <div className="max-w-md mx-auto">
              <div className="w-full bg-gray-700 rounded-full h-6 mb-4">
                <div
                  className="accent-gradient h-6 rounded-full transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="font-bold text-lg">{progress}%</p>
            </div>

            <p className="mt-8 text-sm" style={{ color: 'var(--text-secondary)' }}>
              This may take 2-3 minutes. You'll receive an email when it's ready!
            </p>
          </div>
        )}

        {/* Step 4: Complete Gallery Preview - Design.com Style */}
        {step === 4 && brandKit && (
          <div className="space-y-6">
            {/* Success Header with Download All Button */}
            <div className="p-8 rounded-xl text-center" style={{ backgroundColor: 'var(--card-bg)', border: '2px solid var(--accent-color)' }}>
              <div className="text-6xl mb-4">🎉</div>
              <h2 className="text-3xl font-bold mb-4 gradient-text">Your Complete Brand Kit is Ready!</h2>
              <p className="text-lg mb-6" style={{ color: 'var(--text-secondary)' }}>
                9 professional assets including logo, business cards, social media templates, and more
              </p>
              <button
                onClick={() => handleDownload('complete')}
                className="px-12 py-4 accent-gradient text-white rounded-lg font-bold text-xl btn-glow inline-flex items-center gap-3"
              >
                <span>📦</span> Download Complete Brand Kit (ZIP)
              </button>
            </div>

            {/* Brand Colors Preview */}
            <div className="p-6 rounded-xl" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
              <h3 className="text-xl font-bold mb-4">🎨 Your Brand Colors</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <div 
                    className="w-full h-24 rounded-lg mb-3 border-2 border-white/20"
                    style={{ backgroundColor: brandKit.primary_color }}
                  />
                  <p className="font-bold text-sm">Primary</p>
                  <p className="text-xs font-mono" style={{ color: 'var(--text-secondary)' }}>{brandKit.primary_color}</p>
                </div>
                <div className="text-center">
                  <div 
                    className="w-full h-24 rounded-lg mb-3 border-2 border-white/20"
                    style={{ backgroundColor: brandKit.secondary_color }}
                  />
                  <p className="font-bold text-sm">Secondary</p>
                  <p className="text-xs font-mono" style={{ color: 'var(--text-secondary)' }}>{brandKit.secondary_color}</p>
                </div>
              </div>
            </div>

            {/* Complete Asset Gallery */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              
              {/* Logo */}
              {brandKit.assets?.logo && (
                <div className="p-6 rounded-xl" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                  <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                    <span>📝</span> Logo
                  </h3>
                  <div className="bg-white rounded-lg p-4 mb-4 h-48 flex items-center justify-center">
                    <img 
                      src={brandKit.assets.logo} 
                      alt="Logo"
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                  <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>1024x1024 PNG</p>
                  <button
                    onClick={() => handleDownload('logo')}
                    className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition"
                  >
                    ⬇️ Download
                  </button>
                </div>
              )}

              {/* Business Card Front */}
              {brandKit.assets?.business_card_front && (
                <div className="p-6 rounded-xl" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                  <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                    <span>💼</span> Business Card (Front)
                  </h3>
                  <div className="bg-gray-800 rounded-lg p-2 mb-4 h-48 flex items-center justify-center">
                    <img 
                      src={brandKit.assets.business_card_front} 
                      alt="Business Card Front"
                      className="max-w-full max-h-full object-contain rounded"
                    />
                  </div>
                  <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>3.5" x 2" (300 DPI)</p>
                  <button
                    onClick={() => handleDownload('business-card-front')}
                    className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition"
                  >
                    ⬇️ Download
                  </button>
                </div>
              )}

              {/* Business Card Back */}
              {brandKit.assets?.business_card_back && (
                <div className="p-6 rounded-xl" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                  <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                    <span>💼</span> Business Card (Back)
                  </h3>
                  <div className="bg-gray-800 rounded-lg p-2 mb-4 h-48 flex items-center justify-center">
                    <img 
                      src={brandKit.assets.business_card_back} 
                      alt="Business Card Back"
                      className="max-w-full max-h-full object-contain rounded"
                    />
                  </div>
                  <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>3.5" x 2" (300 DPI)</p>
                  <button
                    onClick={() => handleDownload('business-card-back')}
                    className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition"
                  >
                    ⬇️ Download
                  </button>
                </div>
              )}

              {/* Letterhead */}
              {brandKit.assets?.letterhead && (
                <div className="p-6 rounded-xl" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                  <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                    <span>📄</span> Letterhead
                  </h3>
                  <div className="bg-white rounded-lg p-2 mb-4 h-48 flex items-center justify-center overflow-hidden">
                    <img 
                      src={brandKit.assets.letterhead} 
                      alt="Letterhead"
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                  <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>8.5" x 11" (300 DPI)</p>
                  <button
                    onClick={() => handleDownload('letterhead')}
                    className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition"
                  >
                    ⬇️ Download
                  </button>
                </div>
              )}

              {/* Instagram Post */}
              {brandKit.assets?.instagram_post && (
                <div className="p-6 rounded-xl" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                  <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                    <span>📸</span> Instagram Post
                  </h3>
                  <div className="bg-gray-800 rounded-lg p-2 mb-4 h-48 flex items-center justify-center">
                    <img 
                      src={brandKit.assets.instagram_post} 
                      alt="Instagram Post"
                      className="max-w-full max-h-full object-contain rounded"
                    />
                  </div>
                  <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>1080 x 1080 px</p>
                  <button
                    onClick={() => handleDownload('instagram')}
                    className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition"
                  >
                    ⬇️ Download
                  </button>
                </div>
              )}

              {/* Facebook Cover */}
              {brandKit.assets?.facebook_cover && (
                <div className="p-6 rounded-xl" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                  <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                    <span>📘</span> Facebook Cover
                  </h3>
                  <div className="bg-gray-800 rounded-lg p-2 mb-4 h-48 flex items-center justify-center">
                    <img 
                      src={brandKit.assets.facebook_cover} 
                      alt="Facebook Cover"
                      className="max-w-full max-h-full object-contain rounded"
                    />
                  </div>
                  <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>820 x 312 px</p>
                  <button
                    onClick={() => handleDownload('facebook')}
                    className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition"
                  >
                    ⬇️ Download
                  </button>
                </div>
              )}

              {/* LinkedIn Banner */}
              {brandKit.assets?.linkedin_banner && (
                <div className="p-6 rounded-xl" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                  <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                    <span>💼</span> LinkedIn Banner
                  </h3>
                  <div className="bg-gray-800 rounded-lg p-2 mb-4 h-48 flex items-center justify-center">
                    <img 
                      src={brandKit.assets.linkedin_banner} 
                      alt="LinkedIn Banner"
                      className="max-w-full max-h-full object-contain rounded"
                    />
                  </div>
                  <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>1584 x 396 px</p>
                  <button
                    onClick={() => handleDownload('linkedin')}
                    className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition"
                  >
                    ⬇️ Download
                  </button>
                </div>
              )}

              {/* Email Signature */}
              <div className="p-6 rounded-xl" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <span>✉️</span> Email Signature
                </h3>
                <div className="bg-white rounded-lg p-4 mb-4 h-48 flex items-center justify-center">
                  <div className="text-center text-gray-700 text-xs">
                    <div className="text-2xl mb-2">✉️</div>
                    <p className="font-bold">{brandKit.business_name}</p>
                    {brandKit.tagline && <p className="text-xs">{brandKit.tagline}</p>}
                  </div>
                </div>
                <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>HTML Format</p>
                <button
                  onClick={() => handleDownload('email-signature')}
                  className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition"
                >
                  ⬇️ Download
                </button>
              </div>

              {/* Brand Guide */}
              <div className="p-6 rounded-xl" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <span>📚</span> Brand Guide
                </h3>
                <div className="bg-gray-800 rounded-lg p-4 mb-4 h-48 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-4xl mb-4">📚</div>
                    <p className="text-sm">Colors • Fonts • Usage</p>
                  </div>
                </div>
                <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>Text Document</p>
                <button
                  onClick={() => handleDownload('brand-guide')}
                  className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition"
                >
                  ⬇️ Download
                </button>
              </div>

            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 justify-center pt-6">
              <button
                onClick={() => handleDownload('complete')}
                className="px-10 py-4 accent-gradient text-white rounded-lg font-bold text-lg btn-glow"
              >
                📦 Download Complete Kit (ZIP)
              </button>
              
              <button
                onClick={() => {
                  setStep(1);
                  setBrandKit(null);
                  setFormData({
                    businessName: '',
                    tagline: '',
                    industry: '',
                    description: '',
                    logoStyle: 'modern',
                    colorPreference: 'ai-choose',
                    primaryColor: '#FF6B35',
                    secondaryColor: '#F7931E'
                  });
                }}
                className="px-8 py-4 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-bold text-lg"
              >
                🎨 Create Another Brand Kit
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BrandCreation;
